import api from './api';
import authService from './authService';

// Define the superadmin route prefix
const SUPERADMIN_PREFIX = '/superadmin';

/**
 * Service to handle SuperAdmin API requests
 */
class SuperAdminService {
  /**
   * Check if user is authenticated as a superadmin
   * @returns {Promise<boolean>} Whether the user is authenticated as superadmin
   */
  async isAuthenticated() {
    try {
      const token = authService.getToken();
      if (!token) {
        console.log('No authentication token found');
        return false;
      }
      
      const user = await authService.getCurrentUser();
      
      // Add detailed logging for debugging
      console.log('Token exists:', !!token);
      console.log('User exists:', !!user);
      console.log('User role:', user?.role);
      
      return Boolean(token && user && user.role === 'superadmin');
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  }

  /**
   * Get authentication headers with JWT token
   * @returns {Object} Headers object with authorization token
   */
  getAuthHeaders() {
    const token = authService.getToken();
    
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Add both Authorization and x-auth-token headers for compatibility
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      headers['x-auth-token'] = token;
    }
    
    return { headers };
  }

  /**
   * Check authentication before making requests
   * @throws {Error} If not authenticated as superadmin
   */
  async checkAuth() {
    // For development/debugging: temporarily bypass auth check
    // return true; // Uncomment this line to bypass auth check during development
    
    const isAuth = await this.isAuthenticated();
    if (!isAuth) {
      const error = new Error('You must be logged in as a superadmin to perform this action');
      error.status = 401;
      throw error;
    }
  }

  /**
   * Create a new tenant
   * @param {Object} tenantData - Tenant data including admin user details
   * @returns {Promise<Object>} Created tenant details
   */
  async createTenant(tenantData) {
    try {
      console.log('Starting tenant creation');
      
      // For this specific endpoint, we'll check auth without throwing an exception
      const isAuth = await this.isAuthenticated();
      if (!isAuth) {
        console.warn('User is not authenticated as superadmin but proceeding with request');
      }
      
      // Format tenant data to match backend model requirements
      const formattedData = {
        ...tenantData,
        // Ensure plan is one of the allowed values in the Tenant model
        plan: (tenantData.plan && typeof tenantData.plan === 'string' && 
               ['basic', 'professional', 'enterprise', 'custom'].includes(tenantData.plan)) 
              ? tenantData.plan 
              : 'basic',
        // Just pass subdomain, not domain
        subdomain: tenantData.subdomain,
        domain: tenantData.subdomain
      };
      
      console.log('Formatted tenant data:', formattedData);
      
      const response = await api.post(
        `${SUPERADMIN_PREFIX}/tenants`,
        formattedData,
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error creating tenant:', error);
      throw error;
    }
  }

  /**
   * Check if a subdomain is available
   * @param {string} subdomain - Subdomain to check
   * @returns {Promise<Object>} Response with available status
   */
  async checkSubdomainAvailability(subdomain) {
    try {
      // Don't check authentication for subdomain availability check
      const response = await api.get(
        `${SUPERADMIN_PREFIX}/tenants/check-subdomain/${subdomain}`,
        this.getAuthHeaders()
      );
      
      console.log('Subdomain availability check response:', response.data);
      
      // Ensure we're properly checking the available flag
      return {
        available: response.data.available === true,
        message: response.data.message || 'Subdomain check completed'
      };
    } catch (error) {
      console.error('Error checking subdomain availability:', error);
      
      // Only consider it unavailable if we get an explicit 409 Conflict status
      if (error.response && error.response.status === 409) {
        return { 
          available: false, 
          message: error.response.data?.message || 'This subdomain is already taken' 
        };
      }
      
      // For other errors, assume it might be available and log detailed information
      console.warn('Non-conflict error during subdomain check:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // Default to available for network errors or server issues to prevent blocking the form
      return { 
        available: true, 
        message: 'Could not verify subdomain availability, but you may proceed' 
      };
    }
  }

  /**
   * Get all tenants (requires superadmin auth)
   * @returns {Promise<Array>} List of tenants
   */
  async getAllTenants() {
    try {
      await this.checkAuth();
      const response = await api.get(`${SUPERADMIN_PREFIX}/tenants`, this.getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error fetching tenants:', error);
      throw error;
    }
  }

  /**
   * Get tenant by ID
   * @param {string} id - Tenant ID
   * @returns {Promise<Object>} Tenant details
   */
  async getTenantById(id) {
    try {
      // Make sure we're authenticated
      await this.checkAuth();
      
      // Log the request for debugging
      console.log(`Fetching tenant ${id} from ${SUPERADMIN_PREFIX}/tenants/${id}`);
      console.log('Auth headers:', this.getAuthHeaders());
      
      // Ensure we're using the superadmin endpoint, not the regular tenant endpoint
      const response = await api.get(
        `${SUPERADMIN_PREFIX}/tenants/${id}`,
        this.getAuthHeaders()
      );
      
      console.log('Tenant data received:', response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tenant ${id}:`, error);
      
      // Better error handling with status code
      if (error.response) {
        console.error(`Status: ${error.response.status}, Message:`, error.response.data);
        
        // If unauthorized, try to refresh auth state
        if (error.response.status === 401) {
          console.error('Authentication failed - token may be invalid or expired');
        }
      }
      
      throw error;
    }
  }

  /**
   * Update tenant information
   * @param {string} id - Tenant ID
   * @param {Object} updateData - Updated tenant data
   * @returns {Promise<Object>} Updated tenant
   */
  async updateTenant(id, updateData) {
    try {
      await this.checkAuth();
      
      // Log the request for debugging
      console.log(`Updating tenant ${id} with data:`, updateData);
      
      // Try both possible endpoint structures
      try {
        const response = await api.put(
          `/admin/tenants/${id}`, 
          updateData,
          this.getAuthHeaders()
        );
        return response.data;
      } catch (innerError) {
        if (innerError.response && innerError.response.status === 404) {
          // Try alternate endpoint if first one fails with 404
          console.log('First endpoint not found, trying alternate endpoint');
          
          const response = await api.put(
            `/tenants/${id}`, 
            updateData,
            this.getAuthHeaders()
          );
          return response.data;
        }
        throw innerError;
      }
    } catch (error) {
      console.error(`Error updating tenant ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a tenant
   * @param {string} id - Tenant ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteTenant(id) {
    try {
      await this.checkAuth();
      const response = await api.delete(
        `${SUPERADMIN_PREFIX}/tenants/${id}`, 
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting tenant ${id}:`, error);
      throw error;
    }
  }

  /**
   * Approve a tenant
   * @param {string} id - Tenant ID
   * @returns {Promise<Object>} Approved tenant details
   */
  async approveTenant(id) {
    try {
      await this.checkAuth();
      
      // Log the request for debugging
      console.log(`Approving tenant ${id}`);
      
      // Try both possible endpoint structures
      try {
        const response = await api.put(
          `/admin/tenants/${id}/approve`,
          {},
          this.getAuthHeaders()
        );
        return response.data;
      } catch (innerError) {
        if (innerError.response && innerError.response.status === 404) {
          // Try alternate endpoint if first one fails with 404
          console.log('First endpoint not found, trying alternate endpoint');
          
          const response = await api.put(
            `/tenants/${id}/approve`, 
            {},
            this.getAuthHeaders()
          );
          return response.data;
        }
        throw innerError;
      }
    } catch (error) {
      console.error(`Error approving tenant ${id}:`, error);
      throw error;
    }
  }

  /**
   * Suspend a tenant
   * @param {string} id - Tenant ID
   * @param {string} reason - Suspension reason
   * @returns {Promise<Object>} Suspended tenant details
   */
  async suspendTenant(id, reason) {
    try {
      await this.checkAuth();
      
      // Log the request for debugging
      console.log(`Suspending tenant ${id} with reason: ${reason}`);
      
      // Use the correct endpoint for suspension
      const response = await api.put(
        `${SUPERADMIN_PREFIX}/tenants/${id}/suspend`, 
        { reason },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error suspending tenant ${id}:`, error);
      throw error;
    }
  }

  /**
   * Reactivate a suspended tenant
   * @param {string} id - Tenant ID
   * @returns {Promise<Object>} Reactivated tenant details
   */
  async reactivateTenant(id) {
    try {
      await this.checkAuth();
      
      // Log the request for debugging
      console.log(`Reactivating tenant ${id}`);
      
      // Try both possible endpoint structures
      try {
        const response = await api.put(
          `/admin/tenants/${id}/reactivate`, 
          {},
          this.getAuthHeaders()
        );
        return response.data;
      } catch (innerError) {
        if (innerError.response && innerError.response.status === 404) {
          // Try alternate endpoint if first one fails with 404
          console.log('First endpoint not found, trying alternate endpoint');
          
          const response = await api.put(
            `/tenants/${id}/reactivate`, 
            {},
            this.getAuthHeaders()
          );
          return response.data;
        }
        throw innerError;
      }
    } catch (error) {
      console.error(`Error reactivating tenant ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get tenant usage statistics
   * @param {string} id - Tenant ID
   * @returns {Promise<Object>} Tenant usage stats
   */
  async getTenantUsage(id) {
    try {
      await this.checkAuth();
      const response = await api.get(
        `${SUPERADMIN_PREFIX}/tenants/${id}/usage`, 
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching usage for tenant ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get tenant subscription information
   * @param {string} id - Tenant ID
   * @returns {Promise<Object>} Tenant subscription details
   */
  async getTenantSubscription(id) {
    try {
      await this.checkAuth();
      const response = await api.get(
        `${SUPERADMIN_PREFIX}/tenants/${id}/subscription`, 
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching subscription for tenant ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get superadmin dashboard data
   * @returns {Promise<Object>} Dashboard statistics and data
   */
  async getDashboardData() {
    try {
      await this.checkAuth();
      const response = await api.get(
        `${SUPERADMIN_PREFIX}/dashboard`, 
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }

  // Add a more direct method to check endpoint availability
  async checkApiEndpoints() {
    try {
      // Check various endpoint patterns to determine which one works
      const testPatterns = [
        `/admin/tenants`,
        `/tenants`,
        `/superadmin/tenants`,
        `${SUPERADMIN_PREFIX}/tenants`
      ];
      
      console.log('Checking API endpoint patterns...');
      
      const results = await Promise.allSettled(
        testPatterns.map(url => api.get(url, this.getAuthHeaders()))
      );
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          console.log(`Endpoint ${testPatterns[index]} is valid`);
        } else {
          console.log(`Endpoint ${testPatterns[index]} failed:`, result.reason);
        }
      });
      
      return results;
    } catch (error) {
      console.error('Error checking API endpoints:', error);
      throw error;
    }
  }

  /**
   * Get tenant statistics
   * @param {string} id - Tenant ID
   * @returns {Promise<Object>} Tenant statistics
   */
  async getTenantStats(id) {
    try {
      await this.checkAuth();
      const response = await api.get(
        `${SUPERADMIN_PREFIX}/tenants/${id}/stats`, 
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching stats for tenant ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get tenant administrators
   * @param {string} id - Tenant ID
   * @returns {Promise<Array>} List of tenant administrators
   */
  async getTenantAdmins(id) {
    try {
      await this.checkAuth();
      const response = await api.get(
        `${SUPERADMIN_PREFIX}/tenants/${id}/admins`, 
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching admins for tenant ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get tenant with administrators
   * @param {string} id - Tenant ID
   * @returns {Promise<Object>} Tenant with administrators
   */
  async getTenantWithAdmins(id) {
    try {
      await this.checkAuth();
      const response = await api.get(
        `${SUPERADMIN_PREFIX}/tenants/${id}/with-admins`, 
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching tenant with admins ${id}:`, error);
      throw error;
    }
  }

  /**
   * Reset tenant admin password
   * @param {string} id - Tenant ID
   * @param {string} email - Admin email
   * @returns {Promise<Object>} Response with temporary password
   */
  async resetTenantAdminPassword(id, email) {
    try {
      await this.checkAuth();
      const response = await api.post(
        `${SUPERADMIN_PREFIX}/tenants/${id}/reset-password`,
        { email },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error(`Error resetting password for tenant admin ${id}:`, error);
      throw error;
    }
  }

  /**
   * Update tenant admin
   * @param {string} tenantId - Tenant ID
   * @param {string} adminId - Admin user ID
   * @param {Object} updateData - Updated admin data
   * @returns {Promise<Object>} Updated admin user
   */
  async updateTenantAdmin(tenantId, adminId, updateData) {
    try {
      await this.checkAuth();
      
      // Log the request for debugging
      console.log(`Updating admin ${adminId} for tenant ${tenantId} with data:`, updateData);
      
      // Try both possible endpoint structures
      try {
        const response = await api.put(
          `${SUPERADMIN_PREFIX}/tenants/${tenantId}/admins/${adminId}`,
          updateData,
          this.getAuthHeaders()
        );
        return response.data;
      } catch (innerError) {
        if (innerError.response && innerError.response.status === 404) {
          // Try alternate endpoints
          try {
            const response = await api.put(
              `/admin/tenants/${tenantId}/users/${adminId}`,
              updateData,
              this.getAuthHeaders()
            );
            return response.data;
          } catch (err) {
            // Try one more endpoint pattern
            const response = await api.put(
              `/users/${adminId}`,
              { ...updateData, tenantId },
              this.getAuthHeaders()
            );
            return response.data;
          }
        }
        throw innerError;
      }
    } catch (error) {
      console.error(`Error updating admin ${adminId} for tenant ${tenantId}:`, error);
      throw error;
    }
  }

  /**
   * Create a tenant admin
   * @param {string} tenantId - Tenant ID 
   * @param {Object} adminData - Admin user data
   * @returns {Promise<Object>} Created admin user
   */
  async createTenantAdmin(tenantId, adminData) {
    try {
      await this.checkAuth();
      
      // Log the request for debugging
      console.log(`Creating admin for tenant ${tenantId}:`, adminData);
      
      // Try API endpoint
      const response = await api.post(
        `${SUPERADMIN_PREFIX}/tenants/${tenantId}/admins`,
        adminData,
        this.getAuthHeaders()
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error creating admin for tenant ${tenantId}:`, error);
      throw error;
    }
  }

  /**
   * Change tenant subscription plan
   * @param {string} id - Tenant ID
   * @param {object} planData - Plan change data including plan and notes
   * @returns {Promise<Object>} Updated tenant
   */
  async changeTenantPlan(id, planData) {
    try {
      await this.checkAuth();
      
      // Log the request for debugging
      console.log(`Changing plan for tenant ${id}`, planData);
      
      // Validate the plan value
      const validPlans = ['basic', 'professional', 'enterprise', 'custom'];
      if (!validPlans.includes(planData.plan)) {
        throw new Error(`Invalid plan value: ${planData.plan}. Must be one of: ${validPlans.join(', ')}`);
      }
      
      // Format the request data
      const updateData = {
        plan: planData.plan,
        adminNotes: planData.adminNotes || ''
      };
      
      // First try the superadmin-specific endpoint
      try {
        const response = await api.put(
          `${SUPERADMIN_PREFIX}/tenants/${id}/change-plan`, 
          updateData,
          this.getAuthHeaders()
        );
        return response.data;
      } catch (endpointError) {
        // If the specific endpoint doesn't exist, fall back to the general update endpoint
        if (endpointError.response && endpointError.response.status === 404) {
          console.log('Plan-specific endpoint not found, using general update endpoint');
          return this.updateTenant(id, updateData);
        }
        throw endpointError;
      }
    } catch (error) {
      console.error(`Error changing plan for tenant ${id}:`, error);
      // Enhance error message for easier troubleshooting
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.message;
        throw new Error(`Failed to change plan (${status}): ${message}`);
      }
      throw error;
    }
  }

  /**
   * Get complete tenant details including usage and subscription in a single call
   * @param {string} id - Tenant ID
   * @returns {Promise<Object>} Complete tenant details
   */
  async getTenantDetailsAll(id) {
    try {
      await this.checkAuth();
      
      // Log the request for debugging
      console.log(`Fetching complete tenant details for tenant ${id}`);
      
      // Use the correct endpoint path - no longer using 'direct' in the path
      const response = await api.get(
        `${SUPERADMIN_PREFIX}/direct/tenants/${id}/details-all`,
        this.getAuthHeaders()
      );
      
      // Explicitly add cache timestamp to facilitate cache invalidation if needed
      const responseWithTimestamp = {
        ...response.data,
        _cachedAt: new Date().toISOString()
      };
      
      // Store in sessionStorage for caching
      try {
        sessionStorage.setItem(`tenant_details_${id}`, JSON.stringify(responseWithTimestamp));
      } catch (cacheError) {
        console.warn('Could not cache tenant details:', cacheError);
      }
      
      return responseWithTimestamp;
    } catch (error) {
      console.error(`Error fetching complete tenant details for ${id}:`, error);
      
      // Try to get cached data in case of error
      try {
        const cachedData = sessionStorage.getItem(`tenant_details_${id}`);
        if (cachedData) {
          console.log('Using cached tenant details');
          return JSON.parse(cachedData);
        }
      } catch (cacheError) {
        console.warn('Could not retrieve cached tenant details:', cacheError);
      }
      
      throw error;
    }
  }

  /**
   * Get tenant financial data
   * @param {string} tenantId - Tenant ID
   * @returns {Promise<Object>} Tenant financial data
   */
  async getTenantFinancials(tenantId) {
    const token = authService.getToken();
    
    try {
      const response = await api.get(`/superadmin/tenants/${tenantId}/financials`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant financials:', error);
      throw error;
    }
  }
}

export default new SuperAdminService();